import { useState, useEffect, useRef, useCallback } from 'react';
import apiClient from '../lib/api';
import { useProject } from '../lib/project-context';
import { RequiresProject } from '../components/RequiresContext';
import { useQuery } from '@tanstack/react-query';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import {
  Send, Bot, User, Sparkles, Loader2, Bookmark, BookmarkCheck,
  AlertTriangle, CheckCircle2, Info, TrendingUp, ShieldAlert,
  BarChart3, Target, Zap, FileText, ArrowRight, ChevronRight,
  LineChart, Trash2, X
} from 'lucide-react';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { UpsellCard } from '../components/ModuleSystem';

/* ─── Quick Actions ─── */
const QUICK_ACTIONS = [
  { label: 'Diagnosticar proyecto', icon: Target, question: '¿Este proyecto es viable financieramente? Dame un diagnóstico completo.' },
  { label: 'Explicar riesgos', icon: ShieldAlert, question: '¿Cuáles son los principales riesgos de este proyecto y cómo mitigarlos?' },
  { label: 'Mejorar TIR', icon: TrendingUp, question: '¿Qué acciones concretas puedo tomar para mejorar la TIR de este proyecto?' },
  { label: 'Revisar liquidez', icon: BarChart3, question: '¿Cuál es el mes crítico de liquidez y cuánto capital necesito tener disponible?' },
  { label: 'Sensibilidad', icon: Zap, question: '¿Qué variable afecta más la rentabilidad del proyecto? ¿Cuál es la más sensible?' },
  { label: 'Resumen ejecutivo', icon: FileText, question: 'Genera un resumen ejecutivo completo de este escenario para presentar a inversionistas.' },
];

const SUGGESTIONS = [
  "¿Este proyecto es viable?",
  "¿Cuál es el principal riesgo?",
  "¿Qué pasa si aumento el precio 5%?",
  "¿Qué variable afecta más la TIR?",
  "¿Cuál es el mes crítico de liquidez?",
  "Explícame qué significa peak debt en este proyecto",
  "¿Qué escenario es más equilibrado?",
  "¿Cómo puedo mejorar la rentabilidad?",
];

/* ─── Insight Category Config ─── */
const INSIGHT_CONFIG = {
  positive: { icon: CheckCircle2, color: 'var(--success)', bg: 'var(--success-soft)' },
  warning: { icon: AlertTriangle, color: 'var(--warning-color)', bg: 'var(--warning-soft)' },
  negative: { icon: ShieldAlert, color: 'var(--danger)', bg: 'var(--danger-soft)' },
  info: { icon: Info, color: 'var(--info)', bg: 'var(--info-soft)' },
};

/* ─── KPI Status Colors ─── */
const KPI_STATUS = {
  positive: 'var(--success)',
  warning: 'var(--warning-color)',
  negative: 'var(--danger)',
  neutral: 'var(--text-secondary)',
};

/* ─── Format message text with bold markers ─── */
function FormattedText({ text }) {
  if (!text) return null;
  const lines = text.split('\n');
  return (
    <div className="space-y-1">
      {lines.map((line, i) => {
        if (!line.trim()) return <div key={i} className="h-2" />;
        // Bold sections like **text**
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        const content = parts.map((part, j) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={j} className="font-semibold" style={{ color: 'var(--text-primary)' }}>{part.slice(2, -2)}</strong>;
          }
          return <span key={j}>{part}</span>;
        });
        // Bullet lines
        if (line.trim().startsWith('- ')) {
          return <div key={i} className="flex gap-2 pl-1"><span style={{ color: 'var(--text-muted)' }}>-</span><span className="flex-1">{content.map((c, ci) => <span key={ci}>{ci === 0 && typeof c.props?.children === 'string' ? c.props.children.replace(/^- /, '') : c}</span>)}</span></div>;
        }
        return <div key={i}>{content}</div>;
      })}
    </div>
  );
}

/* ─── Sidebar Panel ─── */
function SidebarPanel({ context, insights, savedInsights, onSaveInsight, onDeleteSavedInsight, loading }) {
  const [tab, setTab] = useState('kpis');

  return (
    <div className="w-[320px] shrink-0 border-l flex flex-col h-full" style={{ borderColor: 'var(--border-default)', background: 'var(--bg-secondary)' }}>
      {/* Tabs */}
      <div className="flex border-b px-1 pt-1" style={{ borderColor: 'var(--border-default)' }}>
        {[{ key: 'kpis', label: 'KPIs' }, { key: 'insights', label: 'Insights' }, { key: 'saved', label: 'Guardados' }].map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className="flex-1 text-xs font-medium py-2.5 transition-colors relative"
            style={{ color: tab === t.key ? 'var(--financial-primary)' : 'var(--text-muted)' }}
            data-testid={`sidebar-tab-${t.key}`}
          >
            {t.label}
            {tab === t.key && <div className="absolute bottom-0 left-2 right-2 h-0.5 rounded-full" style={{ background: 'var(--financial-primary)' }} />}
          </button>
        ))}
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1,2,3].map(i => <div key={i} className="h-16 rounded-lg animate-pulse" style={{ background: 'var(--bg-hover)' }} />)}
          </div>
        ) : tab === 'kpis' ? (
          context?.has_analysis ? (
            <>
              <div className="grid grid-cols-2 gap-2">
                {(context.kpis || []).map(kpi => (
                  <div key={kpi.key} className="rounded-lg p-3 border" style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-primary)' }}>
                    <div className="text-[10px] font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--text-muted)' }}>{kpi.label}</div>
                    <div className="text-sm font-semibold tabular-nums" style={{ color: KPI_STATUS[kpi.status] || 'var(--text-primary)' }}>{kpi.value}</div>
                  </div>
                ))}
              </div>
              {(context.alerts || []).length > 0 && (
                <>
                  <Separator className="my-2" />
                  <div className="text-[10px] font-semibold uppercase tracking-wider mb-2" style={{ color: 'var(--text-muted)' }}>Alertas</div>
                  <div className="space-y-2">
                    {context.alerts.map((a, i) => (
                      <div key={i} className="rounded-lg p-2.5 text-xs border" style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-primary)' }}>
                        <div className="flex items-start gap-2">
                          <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: a.severity === 'critical' ? 'var(--danger)' : 'var(--warning-color)' }} />
                          <div>
                            <div className="font-medium" style={{ color: 'var(--text-primary)' }}>{a.title}</div>
                            <div style={{ color: 'var(--text-muted)' }}>{a.description}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <LineChart className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
              <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Sin análisis ejecutado</p>
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>Ejecuta un análisis en Financial Intelligence para ver KPIs aquí</p>
            </div>
          )
        ) : tab === 'insights' ? (
          insights.length > 0 ? (
            <div className="space-y-2">
              {insights.map((ins, i) => {
                const cfg = INSIGHT_CONFIG[ins.category] || INSIGHT_CONFIG.info;
                const Icon = cfg.icon;
                return (
                  <div key={i} className="rounded-lg p-3 border" style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-primary)' }}>
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: cfg.bg }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>{ins.title}</div>
                        <div className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{ins.content}</div>
                      </div>
                      <button
                        onClick={() => onSaveInsight(ins)}
                        className="shrink-0 p-1 rounded hover:bg-[var(--bg-hover)] transition-colors"
                        title="Guardar insight"
                        data-testid={`save-insight-${i}`}
                      >
                        <Bookmark className="w-3.5 h-3.5" style={{ color: 'var(--text-muted)' }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <Sparkles className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
              <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Sin insights disponibles</p>
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>Los insights se generan automáticamente al tener un análisis</p>
            </div>
          )
        ) : (
          savedInsights.length > 0 ? (
            <div className="space-y-2">
              {savedInsights.map((ins) => {
                const cfg = INSIGHT_CONFIG[ins.category] || INSIGHT_CONFIG.info;
                const Icon = cfg.icon;
                return (
                  <div key={ins.id} className="rounded-lg p-3 border group" style={{ borderColor: 'var(--border-subtle)', background: 'var(--bg-primary)' }}>
                    <div className="flex items-start gap-2">
                      <div className="w-6 h-6 rounded flex items-center justify-center shrink-0" style={{ background: cfg.bg }}>
                        <Icon className="w-3.5 h-3.5" style={{ color: cfg.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs font-semibold mb-0.5" style={{ color: 'var(--text-primary)' }}>{ins.title}</div>
                        <div className="text-[11px] leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{ins.content}</div>
                        <div className="text-[10px] mt-1" style={{ color: 'var(--text-muted)' }}>{new Date(ins.created_at).toLocaleDateString('es-MX')}</div>
                      </div>
                      <button
                        onClick={() => onDeleteSavedInsight(ins.id)}
                        className="shrink-0 p-1 rounded opacity-0 group-hover:opacity-100 hover:bg-[var(--bg-hover)] transition-all"
                        title="Eliminar"
                      >
                        <Trash2 className="w-3 h-3" style={{ color: 'var(--text-muted)' }} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <BookmarkCheck className="w-8 h-8 mx-auto mb-3" style={{ color: 'var(--text-muted)', opacity: 0.4 }} />
              <p className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>Sin insights guardados</p>
              <p className="text-[11px] mt-1" style={{ color: 'var(--text-muted)' }}>Guarda insights desde la pestaña Insights</p>
            </div>
          )
        )}
      </div>
      {/* Module upsell */}
      <div className="shrink-0 p-3 space-y-2" style={{ borderTop: '1px solid var(--border-subtle)' }}>
        <UpsellCard fromModule="copilot" toModule="market" />
        <UpsellCard fromModule="copilot" toModule="construction" />
      </div>
    </div>
  );
}

/* ─── Main Copilot Page ─── */
export default function CopilotPage() {
  const { activeProject, activeScenario, projects: ctxProjects, scenarios: ctxScenarios } = useProject();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [context, setContext] = useState(null);
  const [insights, setInsights] = useState([]);
  const [savedInsights, setSavedInsights] = useState([]);
  const chatEndRef = useRef(null);

  const projectId = activeProject?.id;
  const scenarioId = activeScenario?.id;

  // Load history via React Query
  const { data: historyData, isLoading: historyLoading } = useQuery({
    queryKey: ['copilotHistory', projectId, scenarioId],
    queryFn: () => apiClient.post('/copilot/history', { project_id: projectId, scenario_id: scenarioId || null }).then(r => r.data),
    enabled: !!projectId,
  });

  // Sync history to messages state
  useEffect(() => {
    if (historyData) {
      const history = (historyData || []).reverse().flatMap(h => [
        { role: 'user', text: h.question, id: h.id + '-q' },
        { role: 'assistant', text: h.answer, id: h.id + '-a' },
      ]);
      setMessages(history);
    }
  }, [historyData]);

  // Load context + insights
  const { data: ctxData, isLoading: contextLoading } = useQuery({
    queryKey: ['copilotContext', projectId, scenarioId],
    queryFn: async () => {
      const [ctxRes, insRes, savedRes] = await Promise.all([
        apiClient.post('/copilot/context', { project_id: projectId, scenario_id: scenarioId }),
        apiClient.post('/copilot/insights', { project_id: projectId, scenario_id: scenarioId }),
        apiClient.get(`/copilot/insights/saved?project_id=${projectId}&scenario_id=${scenarioId}`),
      ]);
      return { context: ctxRes.data, insights: insRes.data.insights || [], saved: savedRes.data || [] };
    },
    enabled: !!projectId && !!scenarioId,
  });

  // Sync context data
  useEffect(() => {
    if (ctxData) {
      setContext(ctxData.context);
      setInsights(ctxData.insights);
      setSavedInsights(ctxData.saved);
    }
  }, [ctxData]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = useCallback(async (text) => {
    const q = text || input.trim();
    if (!q || !projectId || !scenarioId) return;
    setInput('');
    setMessages(prev => [...prev, { role: 'user', text: q, id: Date.now() + '-u' }]);
    setLoading(true);
    try {
      const res = await apiClient.post('/copilot/ask', {
        question: q,
        project_id: projectId,
        scenario_id: scenarioId,
      });
      setMessages(prev => [...prev, { role: 'assistant', text: res.data.answer, id: res.data.id + '-a' }]);
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', text: `Error: ${err.response?.data?.detail || 'No se pudo conectar con el copilot.'}`, id: Date.now() + '-e', error: true }]);
    }
    setLoading(false);
  }, [input, projectId, scenarioId]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  };

  const handleSaveInsight = async (insight) => {
    if (!projectId || !scenarioId) return;
    try {
      await apiClient.post('/copilot/insights/save', {
        project_id: projectId,
        scenario_id: scenarioId,
        title: insight.title,
        content: insight.content,
        category: insight.category,
      });
      const res = await apiClient.get(`/copilot/insights/saved?project_id=${projectId}&scenario_id=${scenarioId}`);
      setSavedInsights(res.data || []);
      toast.success('Insight guardado');
    } catch { toast.error('Error al guardar'); }
  };

  const handleDeleteSavedInsight = async (id) => {
    try {
      await apiClient.delete(`/copilot/insights/${id}`);
      setSavedInsights(prev => prev.filter(i => i.id !== id));
      toast.success('Insight eliminado');
    } catch { toast.error('Error al eliminar'); }
  };

  // Guard: no projects
  if (!ctxProjects || ctxProjects.length === 0) {
    return <RequiresProject moduleName="Copilot Financiero"><div /></RequiresProject>;
  }

  const hasContext = !!projectId;
  const hasScenario = !!scenarioId;

  return (
    <div className="flex h-full" data-testid="copilot-page">
      {/* ─── Main Chat Area ─── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <div className="shrink-0 px-6 py-3 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'var(--financial-light)' }}>
              <Sparkles className="w-4 h-4" style={{ color: 'var(--financial-primary)' }} />
            </div>
            <div>
              <h1 className="text-sm font-display font-semibold" style={{ color: 'var(--text-primary)' }}>Copilot Financiero</h1>
              <div className="flex items-center gap-1.5 text-[11px]" style={{ color: 'var(--text-muted)' }}>
                {activeProject ? (
                  <>
                    <span>{activeProject.name}</span>
                    {activeScenario && (
                      <>
                        <ChevronRight className="w-3 h-3" />
                        <span>{activeScenario.name}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span>Selecciona proyecto y escenario</span>
                )}
              </div>
            </div>
          </div>
          {context?.has_analysis && (
            <Badge className="text-[10px] font-medium" style={{ background: 'var(--success-soft)', color: 'var(--success)', border: '1px solid rgba(5,150,105,0.15)' }}>
              Análisis disponible
            </Badge>
          )}
        </div>

        {/* Quick Actions Bar */}
        {hasContext && hasScenario && (
          <div className="shrink-0 px-6 py-2.5 flex gap-2 overflow-x-auto" style={{ borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-primary)' }}>
            {QUICK_ACTIONS.map((action, i) => (
              <button
                key={i}
                onClick={() => handleSend(action.question)}
                disabled={loading}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium whitespace-nowrap shrink-0 transition-all duration-150 hover:shadow-sm"
                style={{ border: '1px solid var(--border-default)', color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}
                onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--financial-primary)'; e.currentTarget.style.color = 'var(--financial-primary)'; }}
                onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                data-testid={`quick-action-${i}`}
              >
                <action.icon className="w-3.5 h-3.5" />
                {action.label}
              </button>
            ))}
          </div>
        )}

        {/* Chat Messages */}
        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="max-w-[780px] mx-auto">
            {!hasContext ? (
              <EmptyState
                icon={<Sparkles className="w-10 h-10" style={{ color: 'var(--financial-primary)', opacity: 0.3 }} />}
                title="Selecciona un proyecto"
                subtitle="Elige un proyecto desde la barra superior para comenzar."
              />
            ) : !hasScenario ? (
              <EmptyState
                icon={<Target className="w-10 h-10" style={{ color: 'var(--financial-primary)', opacity: 0.3 }} />}
                title="Selecciona un escenario"
                subtitle="El Copilot necesita un escenario activo para analizar datos financieros."
              />
            ) : messages.length === 0 && !historyLoading ? (
              /* Welcome state with suggestions */
              <div className="py-8">
                <div className="text-center mb-10">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4" style={{ background: 'var(--financial-light)' }}>
                    <Sparkles className="w-7 h-7" style={{ color: 'var(--financial-primary)' }} />
                  </div>
                  <h2 className="font-display text-lg font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>¿Qué quieres saber?</h2>
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                    Pregunta sobre viabilidad, riesgos, sensibilidad o cualquier aspecto financiero del escenario
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-w-[640px] mx-auto">
                  {SUGGESTIONS.map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(q)}
                      className="text-left p-3.5 rounded-xl text-sm transition-all duration-150 flex items-center gap-3 group"
                      style={{ border: '1px solid var(--border-default)', color: 'var(--text-secondary)', background: 'var(--bg-secondary)' }}
                      onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--financial-primary)'; e.currentTarget.style.color = 'var(--text-primary)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.color = 'var(--text-secondary)'; }}
                      data-testid={`suggestion-${i}`}
                    >
                      <span className="flex-1">{q}</span>
                      <ArrowRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              /* Messages */
              <div className="space-y-5">
                {historyLoading && (
                  <div className="space-y-4">
                    {[1,2].map(i => <div key={i} className="h-20 rounded-xl animate-pulse" style={{ background: 'var(--bg-hover)' }} />)}
                  </div>
                )}
                <AnimatePresence>
                  {messages.map((msg) => (
                    <motion.div
                      key={msg.id}
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.2 }}
                      className={`flex gap-3 ${msg.role === 'user' ? 'justify-end' : ''}`}
                    >
                      {msg.role === 'assistant' && (
                        <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 mt-0.5" style={{ background: msg.error ? 'var(--danger-soft)' : 'var(--financial-light)' }}>
                          <Bot className="w-4 h-4" style={{ color: msg.error ? 'var(--danger)' : 'var(--financial-primary)' }} />
                        </div>
                      )}
                      {msg.role === 'user' ? (
                        <div className="max-w-[80%] rounded-2xl rounded-br-md px-4 py-3 text-sm text-white" style={{ background: 'var(--financial-primary)' }}>
                          {msg.text}
                        </div>
                      ) : (
                        <div
                          className="max-w-[88%] rounded-2xl rounded-bl-md px-5 py-4 text-sm leading-relaxed"
                          style={{
                            background: msg.error ? 'var(--danger-soft)' : 'var(--bg-secondary)',
                            border: `1px solid ${msg.error ? 'rgba(220,38,38,0.15)' : 'var(--border-default)'}`,
                            color: msg.error ? 'var(--danger)' : 'var(--text-secondary)',
                          }}
                        >
                          <FormattedText text={msg.text} />
                        </div>
                      )}
                    </motion.div>
                  ))}
                </AnimatePresence>
                {loading && (
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0" style={{ background: 'var(--financial-light)' }}>
                      <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'var(--financial-primary)' }} />
                    </div>
                    <div className="rounded-2xl rounded-bl-md px-5 py-4 text-sm" style={{ background: 'var(--bg-secondary)', border: '1px solid var(--border-default)', color: 'var(--text-muted)' }}>
                      <div className="flex items-center gap-2">
                        <span>Analizando datos del proyecto</span>
                        <span className="inline-flex gap-0.5">
                          <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: 'var(--financial-primary)', animationDelay: '0ms' }} />
                          <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: 'var(--financial-primary)', animationDelay: '150ms' }} />
                          <span className="w-1 h-1 rounded-full animate-bounce" style={{ background: 'var(--financial-primary)', animationDelay: '300ms' }} />
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <div ref={chatEndRef} />
              </div>
            )}
          </div>
        </div>

        {/* Input */}
        {hasContext && hasScenario && (
          <div className="shrink-0 px-6 py-3" style={{ borderTop: '1px solid var(--border-default)', background: 'var(--bg-secondary)' }}>
            <div className="max-w-[780px] mx-auto">
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Input
                    placeholder="Pregunta sobre tu proyecto..."
                    className="h-11 pr-4 text-sm"
                    style={{ borderColor: 'var(--border-default)', background: 'var(--bg-primary)' }}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={loading}
                    data-testid="copilot-input"
                  />
                </div>
                <Button
                  onClick={() => handleSend()}
                  disabled={!input.trim() || loading}
                  className="h-11 px-5 text-white font-medium"
                  style={{ background: 'var(--accent-primary)' }}
                  onMouseOver={(e) => e.currentTarget.style.background = 'var(--accent-hover)'}
                  onMouseOut={(e) => e.currentTarget.style.background = 'var(--accent-primary)'}
                  data-testid="copilot-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
              {messages.length > 0 && (
                <div className="flex gap-1.5 mt-2 flex-wrap">
                  {SUGGESTIONS.slice(0, 4).map((q, i) => (
                    <button
                      key={i}
                      onClick={() => handleSend(q)}
                      disabled={loading}
                      className="text-[11px] px-2.5 py-1 rounded-full transition-all duration-150"
                      style={{ border: '1px solid var(--border-default)', color: 'var(--text-muted)' }}
                      onMouseOver={(e) => { e.currentTarget.style.borderColor = 'var(--financial-primary)'; e.currentTarget.style.color = 'var(--financial-primary)'; }}
                      onMouseOut={(e) => { e.currentTarget.style.borderColor = 'var(--border-default)'; e.currentTarget.style.color = 'var(--text-muted)'; }}
                    >
                      {q}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ─── Sidebar ─── */}
      {hasContext && hasScenario && (
        <SidebarPanel
          context={context}
          insights={insights}
          savedInsights={savedInsights}
          onSaveInsight={handleSaveInsight}
          onDeleteSavedInsight={handleDeleteSavedInsight}
          loading={contextLoading}
        />
      )}
    </div>
  );
}

/* ─── Empty State ─── */
function EmptyState({ icon, title, subtitle }) {
  return (
    <div className="text-center py-20">
      <div className="mb-4 flex justify-center">{icon}</div>
      <h3 className="font-display text-base font-semibold mb-1" style={{ color: 'var(--text-primary)' }}>{title}</h3>
      <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{subtitle}</p>
    </div>
  );
}
